/**
 * The daily pokes — written by the model, not by anybody's better judgment.
 *
 * The opening sheet's pokes were hand-written for the open and stay on the
 * Opening chip. Once real golf exists, each snapshot writes its own line per
 * man from the facts it can see: where the model has him now, how far he has
 * moved since the open, and what he has actually banked. DERIVED like the
 * snapshots themselves — template picked by a hash of (snapshot, man), so
 * everyone's phone tells the same joke and yesterday's joke never changes.
 */

import { hash32 } from './rank'

export interface PokeFacts {
  name: string
  /** The model's chance now, 0..1. */
  p: number
  /** Chance at the open (de-vigged), 0..1 — null when there was no line. */
  openingP: number | null
  /** Standing in the model's field, 1-based. */
  position: number
  fieldSize: number
  /** Points actually settled so far. */
  points: number
  /** Whether this man is the house. */
  isHouse: boolean
}

const pick = (key: string, pool: string[]): string =>
  pool[hash32(key) % pool.length]!

/** One line per man per snapshot, stable forever for that snapshot. */
export function pokeFor(seedKey: string, f: PokeFacts): string {
  const key = `${seedKey}|${f.name}`
  const delta = f.openingP == null ? 0 : (f.p - f.openingP) * 100
  const pts = f.points

  if (f.isHouse) {
    return pick(key, [
      `The house. Every price on this page is his problem now.`,
      `The house — and the dice still give him ${(f.p * 100).toFixed(0)}%. One week in a while, he keeps everything.`,
    ])
  }

  if (f.position === 1) {
    return pick(key, [
      `Top of the model${pts ? ` with ${pts} points banked` : ''}. Nothing left to prove except Thursday.`,
      `The favorite, says the math. The table is invited to disagree at the usual rates.`,
    ])
  }

  if (delta >= 3) {
    return pick(key, [
      `Up ${delta.toFixed(1)} on the open — the dice noticed before the table did.`,
      `${pts} points settled and climbing. The model no longer files it under luck.`,
      `Shortening fast. Somebody should have bet him yesterday, and knows it.`,
    ])
  }
  if (delta >= 1) {
    return pick(key, [
      `Drifting shorter, quietly cashing what the card gives him.`,
      `Better than the open had him. The kind of move nobody brags about yet.`,
    ])
  }
  if (delta <= -3) {
    return pick(key, [
      `Down ${Math.abs(delta).toFixed(1)} since the open. The committee sends its regards.`,
      `The dice have cooled on him considerably. The dinner table will not let it slide.`,
      `Drifting like a ball in the fescue. Still findable, technically.`,
    ])
  }
  if (delta <= -1) {
    return pick(key, [
      `A little longer than the open. Nothing a signed card would not fix.`,
      `Leaking a point of chance. The model still believes; the ledger is agnostic.`,
    ])
  }

  if (f.position === f.fieldSize) {
    return pick(key, [
      `Longest price in the model. History loves this exact story.`,
      `Bottom of the page, top of the value column — somebody talk himself into it.`,
    ])
  }

  return pick(key, [
    `Exactly where the open had him, which is either control or a stalemate.`,
    `The week has not moved him a hair. The most predictable thing on the property.`,
    `Holding steady${pts ? ` on ${pts} points` : ''}. The model calls it: perfectly priced.`,
  ])
}
